import React, { useState } from "react";

const ExpenseCategory = ({
  kategorie,
  budgetTarget,
  bezahlt,
  budgetiert,
  accounts,
  budgetEntries,
  onBudgetChange,
  maxValue,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(budgetTarget || "");
  const [showAccounts, setShowAccounts] = useState(false);

  // Convert accounts object to sorted array
  const accountsList = Object.values(accounts || {}).sort(
    (a, b) => a.nummer - b.nummer
  );

  const verfuegbar = (budgetTarget || 0) - bezahlt - budgetiert;
  const verbraucht = bezahlt + budgetiert;

  // Scale based on global maxValue (highest of budget or verbraucht across all categories)
  const scale = maxValue || budgetTarget || 1;

  // Budget bar width relative to maxValue
  const budgetWidthPercent = (budgetTarget / scale) * 100;

  // Usage bar: bezahlt + budgetiert relative to maxValue (same scale)
  const bezahltWidthPercent = (bezahlt / scale) * 100;
  const budgetiertWidthPercent = (budgetiert / scale) * 100;

  const formatCHF = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleSave = () => {
    const newAmount = parseFloat(editValue) || 0;
    onBudgetChange(kategorie, newAmount);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(budgetTarget || "");
      setIsEditing(false);
    }
  };

  const handleBarClick = () => {
    // Log all individual expenses for debugging
    console.group(`=== ${kategorie} ===`);

    console.log(`Total Bezahlt (from bookings): ${bezahlt}`);
    console.log(`Total Budgetiert (future): ${budgetiert}`);

    console.group("Bezahlt - from Bookings table:");
    const accts = Object.values(accounts || {});
    accts.forEach((acc) => {
      console.log(`  Konto ${acc.nummer} - ${acc.name}: ${acc.bezahlt}`);
    });
    console.log(`  SUM: ${accts.reduce((sum, acc) => sum + acc.bezahlt, 0)}`);
    console.groupEnd();

    console.group("Budgetiert - from Budget table (future only):");
    (budgetEntries || []).forEach((entry) => {
      const label = entry.bezeichnung || entry.beschreibung || entry.name || "Entry";
      if (entry.skipped) {
        console.log(`  [SKIPPED] ${label} (Konto ${entry.konto}): ${entry.reason}`);
      } else {
        console.log(`  ${label} (Konto ${entry.konto}):`, {
          netto_betrag: entry.netto_betrag,
          typ_timing: entry.typ_timing,
          haeufigkeit: entry.haeufigkeit,
          budgetedAmount: entry.budgetedAmount,
          futureMonths: entry.futureMonths,
        });
      }
    });
    const sumBudgeted = (budgetEntries || []).reduce((sum, e) => sum + (e.budgetedAmount || 0), 0);
    console.log(`  SUM Budgeted: ${sumBudgeted}`);
    console.groupEnd();

    console.groupEnd();

    setShowAccounts(!showAccounts);
  };

  return (
    <div className="expense-category">
      <div className="expense-category-header">
        <span className="expense-category-name">{kategorie}</span>
        <div className="expense-values">
          <div className="values-row">
            {isEditing ? (
              <div className="budget-edit-inline">
                <span>CHF</span>
                <input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  autoFocus
                  min="0"
                  step="1000"
                />
                <button onClick={handleSave} className="btn-save-inline">OK</button>
              </div>
            ) : (
              <span className="budget-value clickable" onClick={() => setIsEditing(true)}>
                CHF {formatCHF(budgetTarget)}
              </span>
            )}
          </div>
          <div className="values-row secondary">
            <span className="verbraucht-value">CHF {formatCHF(verbraucht)}</span>
          </div>
        </div>
      </div>

      <div className="expense-bars">
        {/* Budget bar - scaled to maxValue */}
        <div
          className="expense-bar"
          title={`Budget: CHF ${formatCHF(budgetTarget)}`}
        >
          <div className="bar-segment budget-fill" style={{ width: `${budgetWidthPercent}%` }} />
        </div>

        {/* Usage bar: Bezahlt (red) + Budgetiert (orange) - same scale - clickable */}
        <div
          className={`expense-bar clickable ${showAccounts ? "active" : ""}`}
          title={`Bezahlt: CHF ${formatCHF(bezahlt)}\nBudgetiert: CHF ${formatCHF(budgetiert)}\nVerfügbar: CHF ${formatCHF(verfuegbar)}\nKlicken für Details`}
          onClick={handleBarClick}
        >
          <div className="bar-segment bezahlt" style={{ width: `${bezahltWidthPercent}%` }} />
          <div className="bar-segment budgetiert" style={{ width: `${budgetiertWidthPercent}%` }} />
        </div>
      </div>

      {/* Details list - shown when clicked */}
      {showAccounts && (
        <div className="accounts-list">
          {/* Paid: from bookings table */}
          {accountsList.length > 0 && (
            <div className="section-label">Bezahlt (Buchungen):</div>
          )}
          {accountsList.map((account) => (
            <div key={`acc-${account.nummer}`} className="account-row">
              <span className="account-nummer">{account.nummer}</span>
              <span className="account-name">{account.name}</span>
              <span className="account-amounts">
                <span className="amount-paid">{formatCHF(account.bezahlt)}</span>
              </span>
            </div>
          ))}
          {/* Budgeted: from budget entries (future only) */}
          {(budgetEntries || []).filter(e => e.budgetedAmount > 0 || e.skipped).length > 0 && (
            <div className="section-label">Budgetiert (Budget):</div>
          )}
          {(budgetEntries || []).map((entry, idx) => {
            const label = entry.bezeichnung || entry.beschreibung || entry.name || "Eintrag";
            if (entry.skipped) {
              return (
                <div key={`entry-${idx}`} className="account-row skipped">
                  <span className="account-nummer">{entry.konto}</span>
                  <span className="account-name">{label}</span>
                  <span className="account-status">bereits bezahlt</span>
                </div>
              );
            }
            if (entry.budgetedAmount <= 0) return null;
            return (
              <div key={`entry-${idx}`} className="account-row">
                <span className="account-nummer">{entry.konto}</span>
                <span className="account-name">{label}</span>
                <span className="account-amounts">
                  <span className="amount-budgeted">{formatCHF(entry.budgetedAmount)}</span>
                  {entry.futureMonths !== undefined && (
                    <span className="months-info">({entry.futureMonths} Mt.)</span>
                  )}
                </span>
              </div>
            );
          })}
          {(budgetEntries || []).length === 0 && accountsList.length === 0 && (
            <div className="account-row empty">Keine Einträge vorhanden</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseCategory;
